'use client'

import React from 'react'
import { createPortal } from 'react-dom'

/// A figure with a dotted-underline affordance; hover or keyboard focus
/// reveals a small anchored panel with the breakdown behind the number.
/// Ported from CheekyB's swap kit (which carries the measure-then-place
/// logic this inherits) and dressed in this site's tokens.
///
/// The panel opens at a provisional position and is corrected once it can
/// be MEASURED — guessing the size is what put CheekyB's first version in
/// the wrong place (a right-aligned trigger offset by a fixed max-width
/// drifted left of its own figure). It also flips above the trigger when
/// the viewport bottom would clip it.
export default function HoverDetail({
	children,
	detail,
	heading,
	align = 'left',
	className = ''
}: {
	/// The figure itself — this is what gets the underline.
	children: React.ReactNode
	/// Panel contents. Omit (or null) to render children plainly, no affordance.
	detail?: React.ReactNode
	/// Small uppercase caption at the top of the panel.
	heading?: string
	/// Which edge of the trigger the panel lines up with.
	align?: 'left' | 'right'
	className?: string
}) {
	const ref = React.useRef<HTMLSpanElement>(null)
	const panelRef = React.useRef<HTMLDivElement>(null)
	const [pos, setPos] = React.useState<{ left: number; top: number; ready: boolean } | null>(null)

	const open = () => {
		const el = ref.current
		if (!el || !detail) {
			return
		}
		const r = el.getBoundingClientRect()
		setPos({ left: r.left, top: r.bottom + 8, ready: false })
	}
	const close = () => setPos(null)

	React.useLayoutEffect(() => {
		const panel = panelRef.current
		const trigger = ref.current
		if (!panel || !trigger || !pos || pos.ready) {
			return
		}
		const { width: w, height: h } = panel.getBoundingClientRect()
		const t = trigger.getBoundingClientRect()
		const wanted = align === 'right' ? t.right - w : t.left
		const left = Math.max(8, Math.min(wanted, window.innerWidth - w - 8))
		const below = t.bottom + 8
		const above = t.top - h - 8
		const top = below + h + 8 > window.innerHeight && above >= 8 ? above : below
		setPos({ left, top, ready: true })
	}, [pos, align])

	return (
		<React.Fragment>
			<span
				ref={ref}
				tabIndex={detail ? 0 : -1}
				onMouseEnter={open}
				onMouseLeave={close}
				onFocus={open}
				onBlur={close}
				className={`outline-none ${
					detail
						? 'cursor-help underline decoration-muted-foreground/50 decoration-dotted decoration-2 underline-offset-4 focus-visible:decoration-primary'
						: ''
				} ${className}`}
			>
				{children}
			</span>
			{detail &&
				pos &&
				createPortal(
					<div
						ref={panelRef}
						role="tooltip"
						style={{
							position: 'fixed',
							left: pos.left,
							top: pos.top,
							// Above the app chrome (40) and aligned with the modal layer.
							zIndex: 50,
							maxWidth: 280,
							// Hidden for the one frame between mounting and measuring,
							// so the correction never reads as the panel jumping.
							visibility: pos.ready ? 'visible' : 'hidden'
						}}
						className="pointer-events-none animate-in fade-in slide-in-from-top-1 duration-150 motion-reduce:animate-none"
					>
						{/* alkanes-surface pins the theme tokens but also paints the
						    app background — on the WRAPPER it rendered as a square
						    black slab behind these rounded corners, so it lives on
						    the rounded element itself. */}
						<div className="alkanes-surface rounded-lg border border-border !bg-card px-3 py-2 shadow-lg">
							{heading && (
								<div className="mb-1 text-[9px] font-semibold text-muted-foreground">
									{heading}
								</div>
							)}
							<div className="flex flex-col gap-1 text-xs">{detail}</div>
						</div>
					</div>,
					document.body
				)}
		</React.Fragment>
	)
}
